import { AuthService } from '@src/application/services/auth/AuthService';
import type { IAuthService } from '@src/domain/interfaces/auth/IAuthService';
import type { User as Auth0User } from '@auth0/auth0-react';
import { User } from '@src/domain/entities';

/**
 * Auth0 Service Implementation - Infrastructure layer
 * Implements the authentication service using Auth0
 * Following Single Responsibility Principle - handles only Auth0-specific logic
 * Singleton pattern ensures only one instance exists throughout the application
 */

type Auth0Instance = {
  loginWithRedirect: (options?: { appState?: { returnTo?: string } }) => Promise<void>;
  logout: (options?: { logoutParams?: { returnTo: string } }) => Promise<void>;
  isAuthenticated: boolean;
  user: Auth0User | undefined;
  isLoading: boolean;
  getAccessTokenSilently: () => Promise<string>;
};

export class Auth0Service extends AuthService implements IAuthService {
  private static instance: Auth0Service | null = null;
  private auth0: Auth0Instance | null = null;

  /**
   * Private constructor to prevent direct instantiation
   * Use getInstance() instead
   */
  private constructor() {
    super();
  }

  /**
   * Get the singleton instance of Auth0Service
   * @returns The singleton instance
   */
  public static getInstance(): Auth0Service {
    Auth0Service.instance ??= new Auth0Service();
    return Auth0Service.instance;
  }

  /**
   * Initialize or update the Auth0 instance
   * This should be called when the Auth0 hook values change
   * @param auth0 - The Auth0 instance from useAuth0 hook
   */
  public initialize(auth0: Auth0Instance): void {
    this.auth0 = auth0;
  }

  /**
   * Check if the service has been initialized
   * @returns true if initialized, false otherwise
   */
  public isInitialized(): boolean {
    return this.auth0 !== null;
  }

  async login(): Promise<void> {
    if (!this.auth0) {
      throw new Error('Auth0Service not initialized. Call initialize() first.');
    }
    // Pass current URL so Auth0 redirects back after login
    await this.auth0.loginWithRedirect({
      appState: {
        returnTo: globalThis.location.pathname,
      },
    });
  }

  async logout(): Promise<void> {
    if (!this.auth0) {
      throw new Error('Auth0Service not initialized. Call initialize() first.');
    }
    await this.auth0.logout({
      logoutParams: {
        returnTo: globalThis.location.origin,
      },
    });
  }

  isAuthenticated(): boolean {
    if (!this.auth0) {
      return false;
    }
    return this.auth0.isAuthenticated;
  }

  getUser(): User | null {
    if (!this.auth0?.user) {
      return null;
    }

    return new User({
      id: this.auth0.user.sub || '',
      email: this.auth0.user.email || '',
      name: this.auth0.user.name || '',
      picture: this.auth0.user.picture,
      emailVerified: this.auth0.user.email_verified,
    });
  }

  isLoading(): boolean {
    if (!this.auth0) {
      return true;
    }
    return this.auth0.isLoading;
  }

  async getAccessToken(): Promise<string | null> {
    if (!this.auth0) {
      return null;
    }
    try {
      const token = await this.auth0.getAccessTokenSilently();
      return token;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }
}
